// Room connection sets (docs/plans/admin.md section 3): `?x=<slug>` in the page URL loads a set from the room server
// (GET /experiences/<slug>) before the first room is chosen. The set is applied to rooms.json in memory: its doors lead
// where it says, or are closed; doors it does not name keep their rooms.json link. The set stays active while walking
// because room switches keep `x` in the URL. Rooms keep their pictures and visitors by room id.

import { EXPERIENCE_PARAM, applyExperience, experienceParam, parseExperience, type Experience, type RoomsFile } from '@lr3d/shared';

export type ExperienceLoad =
  | { ok: true; experience: Experience; rooms: RoomsFile }
  | { ok: false; slug: string; message: string };

/**
 * The set named by `url` (`?x=`), checked against `rooms` and applied to it; null when the URL names none. `base`: the
 * room server's HTTP origin (null: single-player, no sets).
 */
export async function loadExperience(base: string | null, rooms: RoomsFile, url: URL = new URL(location.href)): Promise<ExperienceLoad | null> {
  const slug = experienceParam(url);
  if (!slug) return url.searchParams.has(EXPERIENCE_PARAM) ? { ok: false, slug: url.searchParams.get(EXPERIENCE_PARAM) ?? '', message: 'This room set link is not valid.' } : null;
  if (!base) return { ok: false, slug, message: `Room set ${slug} needs the room server; it is not connected.` };
  let status = 0;
  let json: unknown = null;
  try {
    const r = await fetch(`${base}/experiences/${encodeURIComponent(slug)}`, { cache: 'no-store' });
    status = r.status;
    json = await r.json().catch(() => null);
  } catch {
    return { ok: false, slug, message: `Room set ${slug} could not be loaded (the server is not reachable).` };
  }
  if (status === 404) return { ok: false, slug, message: `Room set ${slug} not found.` };
  const p = parseExperience((json as { experience?: unknown } | null)?.experience, rooms);
  if (status !== 200 || !p.ok || p.experience.slug !== slug) return { ok: false, slug, message: `Room set ${slug} could not be used.` };
  return { ok: true, experience: p.experience, rooms: applyExperience(rooms, p.experience) };
}

/** `url` without the set parameter (after a set failed to load). */
export function withoutExperience(url: URL = new URL(location.href)): string {
  const u = new URL(url.href);
  u.searchParams.delete(EXPERIENCE_PARAM);
  return u.pathname + u.search + u.hash;
}
